import { useState, useEffect } from 'react';
import { surveyTemplatesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { Plus, Eye, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';

const SURVEY_TYPES = [
  { id: 'inspection', label: 'Inspection Report' },
  { id: 'damage_assessment', label: 'Damage Assessment' },
  { id: 'condition_survey', label: 'Condition Survey' },
  { id: 'claim_validation', label: 'Claim Validation' },
];

export default function SurveyTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    survey_type: 'inspection',
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await surveyTemplatesApi.getAll();
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await surveyTemplatesApi.create(formData.name, formData.survey_type, formData.description);
      toast.success('Template created with standard building elements');
      setIsDialogOpen(false);
      setFormData({ name: '', survey_type: 'inspection', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const viewTemplate = (template) => {
    setSelectedTemplate(template);
    setIsViewDialogOpen(true);
  };

  const columns = [
    { key: 'name', label: 'Template Name' },
    { key: 'survey_type', label: 'Type', render: (val) => (
      <Badge className="bg-emerald-100 text-emerald-800 capitalize">{val?.replace('_', ' ')}</Badge>
    )},
    { key: 'elements', label: 'Elements', render: (val) => `${val?.length || 0} elements` },
    { key: 'damage_types', label: 'Damage Types', render: (val) => `${val?.length || 0} types` },
    { key: 'is_active', label: 'Status', render: (val) => (
      <Badge className={val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
        {val ? 'Active' : 'Inactive'}
      </Badge>
    )},
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'View Details', icon: Eye, onClick: viewTemplate },
  ];

  return (
    <div className="space-y-6" data-testid="survey-templates-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Survey Templates</h1>
          <p className="text-sm text-stone-500 mt-1">Manage inspection survey templates for surveyors</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-template-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Template
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={templates}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No templates found. Create your first survey template!"
      />

      {/* Create Template Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>Create Survey Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Inspection Report"
                required
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Survey Type *</Label>
              <Select value={formData.survey_type} onValueChange={(val) => setFormData({ ...formData, survey_type: val })}>
                <SelectTrigger data-testid="template-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SURVEY_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={3}
                data-testid="template-description-input"
              />
            </div>
            <p className="text-sm text-stone-500 bg-stone-50 p-3 rounded-lg">
              This template will include standard caravan/park home building elements and damage types with pre-determined descriptions.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="template-submit-btn">
                Create Template
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-template-dialog">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">Survey Type</h4>
              <Badge className="bg-emerald-100 text-emerald-800 capitalize">
                {selectedTemplate?.survey_type?.replace('_', ' ')}
              </Badge>
            </div>
            
            {selectedTemplate?.description && (
              <div>
                <h4 className="font-medium mb-2">Description</h4>
                <p className="text-sm text-stone-600">{selectedTemplate.description}</p>
              </div>
            )}

            <div>
              <h4 className="font-medium mb-2">Building Elements ({selectedTemplate?.elements?.length || 0})</h4>
              <div className="grid grid-cols-2 gap-2">
                {selectedTemplate?.elements?.map((elem, i) => (
                  <div key={i} className="text-sm p-2 bg-stone-50 rounded flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{elem.category}</Badge>
                    {elem.name}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Damage Types ({selectedTemplate?.damage_types?.length || 0})</h4>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate?.damage_types?.map((type, i) => (
                  <Badge key={i} variant="outline" className="capitalize">{type.replace('_', ' ')}</Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
